import React, { useCallback, useEffect, useState } from 'react';
import { Card, Btn, Spinner, Alert, Input, Badge } from '../../components/UI';
import { api } from '../../utils/api';
import { formatRange } from '../../utils/outlookCalendar';

// Grow → Observation & Co-delivery (model UC6): "the currency is hours beside someone better".
// Masters open their delivery days; you ask for a seat; the master confirms your hours after
// the day; confirmed hours count towards certification. How the room is run is never written
// down here — you learn it by watching, then doing.

const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.74rem', color: 'var(--ink-faint)', fontWeight: 600 };
const STATUS_LABEL = { requested: 'Requested', accepted: 'Seat confirmed', declined: 'Declined', attended: 'Counted' };

function OpenDayForm({ onCreated }) {
  const blank = { title: '', units: '', start: '', end: '', location: '', seats: 1, allowCoDelivery: false, notes: '' };
  const [f, setF] = useState(blank);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.observation.openDay({
        title: f.title, units: f.units, location: f.location, seats: f.seats, allowCoDelivery: f.allowCoDelivery, notes: f.notes,
        startsAt: f.start ? new Date(f.start).toISOString() : null, endsAt: f.end ? new Date(f.end).toISOString() : null,
      });
      setF(blank); setOpen(false); onCreated();
    } catch (err) { alert(err.message); } finally { setBusy(false); }
  }
  return (
    <Card style={{ padding: '16px 20px', marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', margin: 0 }}>Open one of your delivery days</h3>
          <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)' }}>As a Master you’re expected to open your days — someone learns the craft by watching you.</div>
        </div>
        <Btn size="sm" variant={open ? 'outline' : 'primary'} onClick={() => setOpen(o => !o)}>{open ? 'Cancel' : '+ Open a day'}</Btn>
      </div>
      {open && (
        <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 14 }}>
          <Input placeholder="What you deliver (e.g. Sales Excellence, day 2)" value={f.title} onChange={e => set('title', e.target.value)} style={{ gridColumn: '1 / -1' }} />
          <label style={labelStyle}>Starts<Input type="datetime-local" value={f.start} onChange={e => set('start', e.target.value)} /></label>
          <label style={labelStyle}>Ends<Input type="datetime-local" value={f.end} min={f.start || undefined} onChange={e => set('end', e.target.value)} /></label>
          <Input placeholder="Units (e.g. IE001, SE004)" value={f.units} onChange={e => set('units', e.target.value)} />
          <Input placeholder="City / location" value={f.location} onChange={e => set('location', e.target.value)} />
          <label style={labelStyle}>Seats<Input type="number" min="1" max="10" value={f.seats} onChange={e => set('seats', e.target.value)} /></label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.86rem', color: 'var(--ink-soft)' }}>
            <input type="checkbox" checked={f.allowCoDelivery} onChange={e => set('allowCoDelivery', e.target.checked)} /> Open for co-delivery too
          </label>
          <Input placeholder="Note for observers (optional — no client confidential details)" value={f.notes} onChange={e => set('notes', e.target.value)} style={{ gridColumn: '1 / -1' }} />
          <Btn type="submit" loading={busy} disabled={!f.title.trim() || !f.start || !f.end} style={{ justifySelf: 'start' }}>Open the day</Btn>
        </form>
      )}
    </Card>
  );
}

function HoursInput({ seat, onDone }) {
  const [h, setH] = useState('');
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <Input type="number" min="0.5" max="24" step="0.5" placeholder="hours" value={h} onChange={e => setH(e.target.value)} style={{ width: 80, padding: '5px 8px' }} />
      <Btn size="sm" disabled={!h} onClick={() => onDone(seat.id, 'attended', Number(h))}>Confirm hours</Btn>
    </span>
  );
}

export default function ObservationPage() {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => { api.observation.list().then(setD).catch(e => setError(e.message)); }, []);
  useEffect(() => { load(); }, [load]);

  async function ask(day, kind) {
    try {
      // eslint-disable-next-line no-alert
      const message = day.myStatus ? null : window.prompt(`Ask ${day.host} for a seat${kind === 'co-deliver' ? ' as co-deliverer' : ''}. A line about why (optional):`, '');
      if (!day.myStatus && message === null) return;
      await api.observation.requestSeat(day.id, kind, message);
      load();
    } catch (e) { alert(e.message); }
  }
  async function decide(seatId, action, hours) { try { await api.observation.decideSeat(seatId, action, hours); load(); } catch (e) { alert(e.message); } }
  async function cancelDay(id) { if (!window.confirm('Cancel this open day? People who asked for a seat will see it disappear.')) return; try { await api.observation.cancelDay(id); load(); } catch (e) { alert(e.message); } }

  if (error) return <Alert type="error">{error}</Alert>;
  if (!d) return <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}><Spinner /></div>;

  const now = new Date();
  const others = d.days.filter(x => !x.mine && new Date(x.endsAt) >= now);
  const mineDays = d.days.filter(x => x.mine);
  const pending = d.requests.filter(r => r.status === 'requested');

  return (
    <>
      <Alert type="info">Hours observed and co-delivered beside a Master are the real currency of certification — not courses passed. How the room is run is not written down anywhere: you learn it here.</Alert>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, margin: '16px 0 20px' }}>
        {[[d.myHours.observed, 'hours observed'], [d.myHours.coDelivered, 'hours co-delivered'], [d.myHours.total, 'hours towards certification'], ...(d.canHost ? [[d.hostedHours, 'hours you gave others']] : [])].map(([n, l]) => (
          <Card key={l} style={{ padding: '14px 16px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', lineHeight: 1 }}>{n}</div>
            <div style={{ fontSize: '0.76rem', color: 'var(--ink-soft)', marginTop: 4 }}>{l}</div>
          </Card>
        ))}
      </div>

      {d.canHost && <OpenDayForm onCreated={load} />}

      {d.canHost && (mineDays.length > 0 || pending.length > 0) && (
        <Card style={{ padding: '16px 20px', marginBottom: 18 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', margin: '0 0 10px' }}>Your open days{pending.length ? ` · ${pending.length} waiting for you` : ''}</h3>
          {mineDays.map(day => {
            const seats = d.requests.filter(r => r.dayId === day.id);
            const started = new Date(day.startsAt) <= now;
            return (
              <div key={day.id} style={{ borderTop: '1px solid var(--line)', padding: '10px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div>
                    <b>{day.title}</b>
                    <div style={{ fontSize: '0.78rem', color: 'var(--ink-soft)' }}>{formatRange(day.startsAt, day.endsAt)}{day.location ? ` · ${day.location}` : ''} · {day.taken}/{day.seats} seats</div>
                  </div>
                  {!started && <Btn size="sm" variant="ghost" onClick={() => cancelDay(day.id)}>Cancel day</Btn>}
                </div>
                {seats.length === 0 && <div style={{ fontSize: '0.8rem', color: 'var(--ink-faint)', marginTop: 4 }}>No requests yet.</div>}
                {seats.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '6px 0 0 12px', fontSize: '0.85rem' }}>
                    <span style={{ flex: 1, minWidth: 180 }}>
                      {s.name}{s.level ? <span style={{ color: 'var(--ink-faint)' }}> · {s.level}</span> : ''} — {s.kind === 'co-deliver' ? 'co-deliver' : 'observe'}
                      {s.message && <span style={{ display: 'block', fontSize: '0.76rem', color: 'var(--ink-soft)' }}>“{s.message}”</span>}
                    </span>
                    <Badge status={s.status === 'attended' ? 'completed' : s.status === 'accepted' ? 'active' : s.status === 'declined' ? 'cancelled' : 'pending'}>{STATUS_LABEL[s.status]}{s.hours ? ` · ${s.hours} h` : ''}</Badge>
                    {s.status === 'requested' && <>
                      <Btn size="sm" onClick={() => decide(s.id, 'accept')}>Accept</Btn>
                      <Btn size="sm" variant="ghost" onClick={() => decide(s.id, 'decline')}>Decline</Btn>
                    </>}
                    {s.status === 'accepted' && started && <HoursInput seat={s} onDone={decide} />}
                  </div>
                ))}
              </div>
            );
          })}
        </Card>
      )}

      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', margin: '0 0 10px' }}>Open days you can join</h3>
      {others.length === 0 ? (
        <Card style={{ padding: '18px 20px', color: 'var(--ink-soft)', fontSize: '0.88rem' }}>
          No open days right now. Masters open their delivery days here — check back, or ask a Master directly in Community → Chat.
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {others.map(day => {
            const full = day.taken >= day.seats;
            return (
              <Card key={day.id} style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.02rem' }}>{day.title}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)' }}>{formatRange(day.startsAt, day.endsAt)}{day.location ? ` · ${day.location}` : ''}</div>
                <div style={{ fontSize: '0.8rem' }}>with <b>{day.host}</b>{day.hostCountry ? ` · ${day.hostCountry}` : ''}</div>
                {day.units && <div style={{ fontSize: '0.76rem', color: 'var(--ink-faint)' }}>Units: {day.units}</div>}
                {day.notes && <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)' }}>{day.notes}</div>}
                <div style={{ fontSize: '0.74rem', color: 'var(--ink-faint)' }}>{day.taken}/{day.seats} seats taken{day.allowCoDelivery ? ' · co-delivery possible' : ''}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4, alignItems: 'center' }}>
                  {day.myStatus ? (
                    <>
                      <Badge status={day.myStatus === 'attended' ? 'completed' : day.myStatus === 'accepted' ? 'active' : day.myStatus === 'declined' ? 'cancelled' : 'pending'}>{STATUS_LABEL[day.myStatus]}{day.myKind === 'co-deliver' ? ' · co-deliver' : ''}</Badge>
                      {day.myStatus !== 'attended' && <Btn size="sm" variant="ghost" onClick={() => ask(day)}>Withdraw</Btn>}
                    </>
                  ) : full ? (
                    <span style={{ fontSize: '0.8rem', color: 'var(--ink-faint)' }}>Full</span>
                  ) : (
                    <>
                      <Btn size="sm" onClick={() => ask(day, 'observe')}>Ask for a seat</Btn>
                      {day.allowCoDelivery && <Btn size="sm" variant="outline" onClick={() => ask(day, 'co-deliver')}>Ask to co-deliver</Btn>}
                    </>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}
